import React, { useCallback, useMemo, useState } from "react";
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Form,
  Modal,
  Nav,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Clipboard,
  Cpu,
  RefreshCw,
  TrendingDown,
} from "react-feather";
import { useGet } from "../../api";
import {
  IJourneyCoverageGapResponse,
  IJourneyMerchantListResponse,
  IJourneyMerchantRow,
  IJourneyMissingVerdictResponse,
  IJourneyNodeResult,
  IJourneyRunListResponse,
  IJourneyRunRow,
  IQaReviewListsResponse,
  IJourneyRunTotals,
  TRunOutcome,
  TRunSort,
} from "../../interfaces";

// ─── Filter model ────────────────────────────────────────────────────────────

type TTab = "merchants" | "runs" | "coverage";

/**
 * The whole page is a function of this one object, so a preset is just a value
 * and the "what am I looking at" line can be rendered from it honestly. Dates
 * are stored as concrete ISO instants rather than a relative key — a relative
 * window recomputed every render would change the request URL on each pass and
 * refetch forever, and a reviewer needs to be able to say WHICH window they
 * reviewed.
 */
interface IFilters {
  tab: TTab;
  domain: string;
  /** A QA list's id — the results are narrowed to the merchants it names. */
  qaListId: string;
  from: string;
  to: string;
  outcomes: TRunOutcome[];
  ai: "" | "yes" | "no";
  source: string;
  device: string;
  minDurationMs: number | null;
  minCost: number | null;
  sort: TRunSort;
  regressedOnly: boolean;
  page: number;
}

const HOUR = 3600 * 1000;
const agoIso = (ms: number): string => new Date(Date.now() - ms).toISOString();

const PAGE_SIZE = 50;

/**
 * Built on demand, never as a module constant: the window has to be measured
 * from when the reviewer clicks, not from when the bundle loaded. A tab left
 * open overnight would otherwise hand out a "last 24h" that ended yesterday.
 */
const defaultFilters = (): IFilters => ({
  tab: "merchants",
  domain: "",
  qaListId: "",
  from: agoIso(7 * 24 * HOUR),
  to: "",
  outcomes: [],
  ai: "",
  source: "",
  device: "",
  minDurationMs: null,
  minCost: null,
  sort: "recent",
  regressedOnly: false,
  page: 0,
});

// Quick windows. "review by date" is the custom pair below these.
const RANGES: Array<{ label: string; ms: number }> = [
  { label: "24h", ms: 24 * HOUR },
  { label: "7d", ms: 7 * 24 * HOUR },
  { label: "30d", ms: 30 * 24 * HOUR },
  { label: "90d", ms: 90 * 24 * HOUR },
];

// "Too long" thresholds. A journey walk is minutes, not seconds, so the useful
// cut-offs start where a run has clearly stopped being routine.
const SLOW_OPTIONS: Array<{ label: string; ms: number | null }> = [
  { label: "Any duration", ms: null },
  { label: "Over 2 min", ms: 2 * 60_000 },
  { label: "Over 5 min", ms: 5 * 60_000 },
  { label: "Over 10 min", ms: 10 * 60_000 },
  { label: "Over 20 min", ms: 20 * 60_000 },
];

/**
 * The slices the page is actually opened for. Each one is a complete filter
 * state, not a delta — clicking a preset always lands you somewhere you can
 * describe, with no residue from whatever was set before.
 */
const PRESETS: Array<{
  key: string;
  label: string;
  hint: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  build: () => IFilters;
}> = [
  {
    key: "regressed",
    label: "Broke since yesterday",
    hint: "Merchants that passed last time and don't now — the overnight triage queue.",
    icon: TrendingDown,
    build: () => ({
      ...defaultFilters(),
      tab: "merchants",
      from: agoIso(24 * HOUR),
      regressedOnly: true,
    }),
  },
  {
    key: "needs_human",
    label: "Needs a human",
    hint: "Partial and failing merchants over the last week, worst first.",
    icon: AlertTriangle,
    build: () => ({
      ...defaultFilters(),
      tab: "merchants",
      outcomes: ["partial", "failure"],
      sort: "worst",
    }),
  },
  {
    key: "expensive",
    label: "Expensive / slow",
    hint: "Runs ranked by AI spend — pair with the duration filter to find the drags.",
    icon: Cpu,
    build: () => ({
      ...defaultFilters(),
      tab: "runs",
      sort: "costliest",
    }),
  },
  {
    key: "unverified",
    label: "No verdict",
    hint: "Merchants that ran and wrote nothing, plus maps with no run in the window — coverage holes, not regressions.",
    icon: AlertTriangle,
    build: () => ({ ...defaultFilters(), tab: "coverage" }),
  },
];

// ─── Badges ──────────────────────────────────────────────────────────────────

const OUTCOME_META: Record<
  TRunOutcome,
  { label: string; bg: string; text?: "dark" }
> = {
  success: { label: "success", bg: "success" },
  partial: { label: "partial", bg: "warning", text: "dark" },
  failure: { label: "failure", bg: "danger" },
  not_tested: { label: "not tested", bg: "light", text: "dark" },
  needs_human: { label: "needs human", bg: "info", text: "dark" },
  pending: { label: "running", bg: "secondary" },
  error: { label: "error", bg: "dark" },
};

const OUTCOME_ORDER: TRunOutcome[] = [
  "success",
  "partial",
  "failure",
  "needs_human",
  "not_tested",
  "error",
];

const OutcomeBadge: React.FC<{ outcome?: TRunOutcome | null }> = ({
  outcome,
}) => {
  const m = OUTCOME_META[outcome ?? "not_tested"] ?? OUTCOME_META.not_tested;
  return (
    <Badge bg={m.bg} text={m.text}>
      {m.label}
    </Badge>
  );
};

const VERDICT_META: Record<string, { label: string; bg: string; text?: "dark" }> =
  {
    pass: { label: "pass", bg: "success" },
    fail: { label: "fail", bg: "danger" },
    healed: { label: "healed", bg: "info" },
    not_tested: { label: "not tested", bg: "light", text: "dark" },
  };

const VerdictBadge: React.FC<{ verdict?: string | null }> = ({ verdict }) => {
  const m = VERDICT_META[verdict ?? "not_tested"] ?? VERDICT_META.not_tested;
  return (
    <Badge bg={m.bg} text={m.text}>
      {m.label}
    </Badge>
  );
};

const SOURCE_META: Record<string, { label: string; bg: string; text?: "dark" }> =
  {
    bespoke: { label: "bespoke", bg: "primary" },
    template: { label: "template", bg: "success" },
    platform_default: { label: "platform", bg: "secondary" },
    platform_override: { label: "platform+override", bg: "info" },
    unmapped: { label: "unmapped", bg: "light", text: "dark" },
  };

const SourceBadge: React.FC<{ source?: string | null }> = ({ source }) => {
  const m = SOURCE_META[source ?? "unmapped"] ?? SOURCE_META.unmapped;
  return (
    <Badge bg={m.bg} text={m.text}>
      {m.label}
    </Badge>
  );
};

// Which device the browser emulated for the run — mobile is prioritised, so it
// gets the accent colour; desktop is muted.
const DeviceBadge: React.FC<{ device?: string | null }> = ({ device }) => {
  const isMobile = device === "mobile";
  return (
    <Badge bg={isMobile ? "primary" : "secondary"}>
      {isMobile ? "mobile" : "desktop"}
    </Badge>
  );
};

/** Green tick when AI never ran; a cost chip when it did. AI-free runs are the
 *  cheap steady state, so they should read as the good case. */
const AiBadge: React.FC<{ used?: boolean; cost?: number | null }> = ({
  used,
  cost,
}) =>
  used ? (
    <Badge bg="info">
      <Cpu size={11} className="me-1" />
      {fmtCost(cost)}
    </Badge>
  ) : (
    <span className="text-muted small">no AI</span>
  );

// ─── Formatting ──────────────────────────────────────────────────────────────

const fmtDate = (d: Date | string | null | undefined): string =>
  d ? new Date(d).toLocaleString() : "—";

const fmtDateShort = (d: Date | string | null | undefined): string =>
  d
    ? new Date(d).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/** Milliseconds are unreadable at journey-walk scale; minutes and seconds are. */
const fmtDuration = (ms: number | null | undefined): string => {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
};

/** Four decimals — per-run AI spend is routinely under a cent. */
function fmtCost(n: number | null | undefined): string {
  if (n == null) return "—";
  return `$${n.toFixed(4)}`;
}

const fmtMoney = (n: number | null | undefined): string =>
  n == null ? "—" : `$${n.toFixed(2)}`;

/** `<input type="datetime-local">` wants local wall-clock with no zone. */
const isoToLocalInput = (iso: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const localInputToIso = (v: string): string =>
  v ? new Date(v).toISOString() : "";

// ─── Copy to clipboard ───────────────────────────────────────────────────────

/** Escape a value for a markdown table cell (pipes, and newlines that would
 *  otherwise break the row). */
const cell = (v: string | null | undefined): string =>
  (v ?? "")
    .replace(/\|/g, "\\|")
    .replace(/\s*\n\s*/g, " ")
    .trim() || "—";

/**
 * One run as markdown — everything the detail modal shows, in a form that can
 * be pasted straight into a chat/ticket for diagnosis. The `note` (the
 * coupon-apply outcome) and each node's matched VALUE are the two fields that
 * usually explain a verdict, so both are included verbatim.
 */
const runToMarkdown = (run: IJourneyRunRow): string => {
  const lines: string[] = [
    `# Template Result — ${run.domain} (${run.templateView})`,
    "",
    `- Run id: ${run._id}`,
    `- Device: ${run.device}`,
    `- Source: ${run.source}${
      run.checkoutTechnology ? ` / ${run.checkoutTechnology}` : ""
    }`,
    `- Country: ${run.countryCode ?? "—"}`,
    `- Tier: ${run.tier} · Status: ${run.status} · Outcome: ${run.outcome}`,
    `- Nodes: ${run.passCount} pass · ${run.failCount} fail (${run.businessFailCount} business) · ${run.healedCount} healed · ${run.notTestedCount} not tested`,
    `- Duration: ${fmtDuration(run.durationMs)}`,
    `- Ran at: ${fmtDate(run.createdAt)}`,
    `- AI: ${run.aiUsed ? "used" : "not used"} · cost ${fmtCost(run.aiCost)}${
      run.visionCalls ? ` · vision ${run.visionCalls} calls` : ""
    }`,
  ];
  if (run.note) lines.push(`- Note: ${run.note}`);
  if (run.userAgent) lines.push(`- User agent: ${run.userAgent}`);
  lines.push(
    "",
    "| Node | Selector | Matches | Value | Verdict | Reason |",
    "| --- | --- | ---: | --- | --- | --- |"
  );
  for (const n of run.nodeResults ?? []) {
    const selector = n.healedTo
      ? `${n.selector ?? "—"} → ${n.healedTo}`
      : n.selector;
    const value = [n.matchedTag ? `<${n.matchedTag}>` : "", n.matchedText ?? ""]
      .filter(Boolean)
      .join(" ");
    const verdict = n.failureClass
      ? `${n.verdict} (${n.failureClass})`
      : n.verdict;
    lines.push(
      `| ${cell(n.node)} | ${cell(selector)} | ${
        n.resolvedCount ?? "—"
      } | ${cell(value)} | ${verdict} | ${cell(n.reason)} |`
    );
  }
  return lines.join("\n");
};

/** The filtered list, one row each — a sweep-level view rather than a single
 *  run's detail. Carries the filter description and the cost/time rollup so a
 *  pasted report is self-explaining. */
const rowsToMarkdown = (
  runs: IJourneyRunRow[],
  describe: string,
  totals: IJourneyRunTotals | undefined
): string => {
  const lines: string[] = [
    `# Template Results — ${runs.length} row(s)`,
    "",
    `- Filters: ${describe}`,
    `- Copied at: ${new Date().toLocaleString()}`,
  ];
  if (totals) {
    lines.push(
      `- Window: ${totals.runs} runs across ${totals.merchants} merchants`,
      `- AI: ${fmtMoney(totals.aiCost)} total, ${totals.aiRuns} of ${
        totals.runs
      } runs used AI (${totals.pctAiRuns ?? 0}%)`,
      `- Time: median ${fmtDuration(totals.medianDurationMs)} · p95 ${fmtDuration(
        totals.p95DurationMs
      )} · max ${fmtDuration(totals.maxDurationMs)}`
    );
  }
  lines.push(
    "",
    "| Domain | View | Device | Source | Outcome | Pass | Fail | AI cost | Duration | Ran at | Note |",
    "| --- | --- | --- | --- | --- | ---: | ---: | ---: | ---: | --- | --- |"
  );
  for (const run of runs) {
    lines.push(
      `| ${cell(run.domain)} | ${cell(run.templateView)} | ${run.device} | ${
        run.source
      } | ${run.outcome} | ${run.passCount} | ${run.failCount} | ${fmtCost(
        run.aiCost
      )} | ${fmtDuration(run.durationMs)} | ${fmtDate(run.createdAt)} | ${cell(
        run.note
      )} |`
    );
  }
  return lines.join("\n");
};

/** `navigator.clipboard` is unavailable on insecure origins and can reject when
 *  the document isn't focused, so fall back to the textarea + execCommand path
 *  rather than failing silently. */
const copyText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const CopyButton: React.FC<{
  /** Lazy so a large run is only serialised on click, not on every render. */
  text: () => string;
  label?: string;
  variant?: string;
  disabled?: boolean;
}> = ({ text, label = "Copy", variant = "outline-secondary", disabled }) => {
  const [state, setState] = useState<"idle" | "ok" | "err">("idle");

  const onClick = async () => {
    const ok = await copyText(text());
    setState(ok ? "ok" : "err");
    window.setTimeout(() => setState("idle"), 2000);
  };

  return (
    <Button
      variant={state === "err" ? "outline-danger" : variant}
      size="sm"
      onClick={onClick}
      disabled={disabled}
    >
      {state === "ok" ? (
        <Check size={14} className="me-1" />
      ) : (
        <Clipboard size={14} className="me-1" />
      )}
      {state === "ok" ? "Copied" : state === "err" ? "Copy failed" : label}
    </Button>
  );
};

// ─── Detail modal — the per-node journey rows ────────────────────────────────

const RunDetailModal: React.FC<{
  run: IJourneyRunRow | null;
  onClose: () => void;
}> = ({ run, onClose }) => (
  <Modal show={!!run} onHide={onClose} size="xl">
    <Modal.Header closeButton>
      <Modal.Title>
        {run?.domain}{" "}
        <span className="text-muted small">/ {run?.templateView}</span>{" "}
        {run && <DeviceBadge device={run.device} />}{" "}
        {run && <SourceBadge source={run.source} />}{" "}
        {run && <OutcomeBadge outcome={run.outcome} />}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {run && (
        <>
          <Row className="mb-3 g-2">
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Tier</small>
              <strong>{run.tier}</strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Status</small>
              <strong>{run.status}</strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Duration</small>
              <strong>{fmtDuration(run.durationMs)}</strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Ran at</small>
              <strong>{fmtDate(run.createdAt)}</strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Nodes</small>
              <strong>
                <span className="text-success">{run.passCount}✓</span>{" "}
                <span className="text-danger">{run.failCount}✗</span>{" "}
                <span className="text-info">{run.healedCount}↻</span>{" "}
                <span className="text-muted">{run.notTestedCount}·</span>
              </strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Real selector rot</small>
              <strong>
                {run.businessFailCount} of {run.failCount} fails
              </strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">AI cost</small>
              <strong>
                {fmtCost(run.aiCost)}
                {run.visionCalls
                  ? ` · vision ${run.visionCalls} (${fmtCost(run.visionCost)})`
                  : ""}
              </strong>
            </Col>
            <Col xs={6} md={3}>
              <small className="text-muted d-block">Country</small>
              <strong>{run.countryCode ?? "—"}</strong>
            </Col>
          </Row>
          {run.note && (
            <div className="alert alert-info py-2 px-3 small mb-3" role="alert">
              {run.note}
            </div>
          )}
          {run.userAgent && (
            <div className="mb-3 small text-muted">
              <span className="d-block">User agent</span>
              <code className="text-break">{run.userAgent}</code>
            </div>
          )}
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Node</th>
                <th>Selector</th>
                <th className="text-end">Matches</th>
                <th>Value</th>
                <th>Verdict</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {(run.nodeResults ?? []).map((n: IJourneyNodeResult, i: number) => (
                <tr key={`${n.node}-${i}`}>
                  <td className="small">{n.node}</td>
                  <td
                    className="font-monospace small text-break"
                    style={{ maxWidth: 320 }}
                  >
                    {n.selector ?? <span className="text-muted">—</span>}
                    {n.healedTo && (
                      <div className="text-success small">
                        → fallback: {n.healedTo}
                      </div>
                    )}
                  </td>
                  <td className="text-end">
                    {n.resolvedCount == null ? "—" : n.resolvedCount}
                  </td>
                  <td className="small text-break" style={{ maxWidth: 260 }}>
                    {n.matchedText || n.matchedTag ? (
                      <>
                        {n.matchedTag && (
                          <span className="text-muted font-monospace">
                            {`<${n.matchedTag}>`}
                          </span>
                        )}{" "}
                        {n.matchedText && <span>{n.matchedText}</span>}
                      </>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <VerdictBadge verdict={n.verdict} />
                    {n.failureClass && (
                      <div className="text-muted small">{n.failureClass}</div>
                    )}
                  </td>
                  <td className="small text-muted">{n.reason ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Modal.Body>
    <Modal.Footer className="justify-content-between">
      <small className="text-muted">
        Copies this run — header, note and every node row — as markdown.
      </small>
      <div className="d-flex gap-2">
        {run && (
          <CopyButton
            text={() => runToMarkdown(run)}
            label="Copy run"
            variant="primary"
          />
        )}
        <Button variant="outline-secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal.Footer>
  </Modal>
);

// ─── Small presentational pieces ─────────────────────────────────────────────

const StatTile: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: "danger" | "warning" | "muted";
}> = ({ label, value, sub, tone }) => (
  <Card className="h-100">
    <Card.Body className="py-2">
      <div className="text-muted small">{label}</div>
      <div
        className={`fs-5 fw-semibold ${tone ? `text-${tone}` : ""}`}
      >
        {value}
      </div>
      {sub && <div className="text-muted small">{sub}</div>}
    </Card.Body>
  </Card>
);

const NodeCounts: React.FC<{ run: IJourneyRunRow }> = ({ run }) => (
  <span className="small text-nowrap">
    <span className="text-success">{run.passCount}✓</span>{" "}
    <span className={run.failCount ? "text-danger" : "text-muted"}>
      {run.failCount}✗
    </span>{" "}
    {run.healedCount > 0 && <span className="text-info">{run.healedCount}↻ </span>}
    <span className="text-muted">{run.notTestedCount}·</span>
  </span>
);

/** Oldest → newest outcome dots, so a merchant's recent trajectory is legible
 *  without opening anything. */
const HistoryStrip: React.FC<{ row: IJourneyMerchantRow }> = ({ row }) => (
  <span className="text-nowrap">
    {[...(row.history ?? [])]
      .slice(0, 8)
      .reverse()
      .map((h) => (
        <OverlayTrigger
          key={h._id}
          placement="top"
          overlay={
            <Tooltip>
              {h.outcome} · {fmtDateShort(h.createdAt)} ·{" "}
              {fmtDuration(h.durationMs)}
            </Tooltip>
          }
        >
          <span
            className={`d-inline-block rounded-circle me-1 bg-${
              OUTCOME_META[h.outcome]?.bg ?? "light"
            }`}
            style={{ width: 8, height: 8 }}
          />
        </OverlayTrigger>
      ))}
  </span>
);

const TotalsRow: React.FC<{
  totals?: IJourneyRunTotals;
  loading: boolean;
  merchantView: boolean;
}> = ({ totals, loading, merchantView }) => {
  const spin = loading ? <Spinner size="sm" /> : null;
  const bad = (totals?.byOutcome?.failure ?? 0) + (totals?.byOutcome?.error ?? 0);
  const partial = totals?.byOutcome?.partial ?? 0;
  return (
    <Row className="g-2 mb-3">
      <Col xs={6} md={2}>
        <StatTile
          label={merchantView ? "Merchants" : "Runs"}
          value={spin ?? (merchantView ? totals?.merchants ?? 0 : totals?.runs ?? 0)}
          sub={
            merchantView
              ? `${totals?.runs ?? 0} runs in window`
              : `${totals?.merchants ?? 0} merchants`
          }
        />
      </Col>
      <Col xs={6} md={2}>
        <StatTile
          label="Passing"
          value={spin ?? (totals?.byOutcome?.success ?? 0)}
        />
      </Col>
      <Col xs={6} md={2}>
        <StatTile
          label="Partial"
          value={spin ?? partial}
          tone={partial ? "warning" : "muted"}
        />
      </Col>
      <Col xs={6} md={2}>
        <StatTile
          label={merchantView ? "Failing / regressed" : "Failing"}
          value={spin ?? bad}
          sub={
            merchantView && totals?.regressions != null
              ? `${totals.regressions} regressed`
              : undefined
          }
          tone={bad ? "danger" : "muted"}
        />
      </Col>
      <Col xs={6} md={2}>
        <StatTile
          label="AI cost"
          value={spin ?? fmtMoney(totals?.aiCost)}
          sub={
            totals
              ? `${totals.aiRuns}/${totals.runs} runs used AI (${
                  totals.pctAiRuns ?? 0
                }%)`
              : undefined
          }
        />
      </Col>
      <Col xs={6} md={2}>
        <StatTile
          label="Run time"
          value={spin ?? fmtDuration(totals?.medianDurationMs)}
          sub={
            totals
              ? `p95 ${fmtDuration(totals.p95DurationMs)} · max ${fmtDuration(
                  totals.maxDurationMs
                )}`
              : undefined
          }
        />
      </Col>
    </Row>
  );
};

const Pager: React.FC<{
  page: number;
  limit: number;
  total: number;
  onPage: (p: number) => void;
}> = ({ page, limit, total, onPage }) => {
  const start = total === 0 ? 0 : page * limit + 1;
  const end = Math.min(total, (page + 1) * limit);
  return (
    <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
      <small className="text-muted">
        {start}–{end} of {total}
      </small>
      <ButtonGroup size="sm">
        <Button
          variant="outline-secondary"
          disabled={page === 0}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft size={14} />
        </Button>
        <Button
          variant="outline-secondary"
          disabled={end >= total}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight size={14} />
        </Button>
      </ButtonGroup>
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

export const TemplateResults: React.FC = () => {
  const [filters, setFilters] = useState<IFilters>(defaultFilters);
  const [selected, setSelected] = useState<IJourneyRunRow | null>(null);

  // Any filter change resets paging — otherwise you land on page 4 of a
  // three-page result and see an empty table.
  const patch = useCallback(
    (p: Partial<IFilters>) =>
      setFilters((f) => ({ ...f, ...p, page: p.page ?? 0 })),
    []
  );

  const toggleOutcome = useCallback(
    (o: TRunOutcome) =>
      setFilters((f) => ({
        ...f,
        page: 0,
        outcomes: f.outcomes.includes(o)
          ? f.outcomes.filter((x) => x !== o)
          : [...f.outcomes, o],
      })),
    []
  );

  /** Shared query string for the runs and merchant endpoints. */
  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.domain) p.set("domain", filters.domain);
    if (filters.qaListId) p.set("qaListId", filters.qaListId);
    if (filters.from) p.set("from", filters.from);
    if (filters.to) p.set("to", filters.to);
    if (filters.outcomes.length) p.set("outcome", filters.outcomes.join(","));
    if (filters.ai) p.set("aiUsed", filters.ai === "yes" ? "true" : "false");
    if (filters.source) p.set("source", filters.source);
    if (filters.device) p.set("device", filters.device);
    if (filters.minDurationMs != null)
      p.set("minDurationMs", String(filters.minDurationMs));
    if (filters.minCost != null) p.set("minCost", String(filters.minCost));
    p.set("sort", filters.sort);
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String(filters.page * PAGE_SIZE));
    return p;
  }, [filters]);

  const merchantUrl = useMemo(() => {
    if (filters.tab !== "merchants") return null;
    const p = new URLSearchParams(query);
    if (filters.regressedOnly) p.set("regressedOnly", "true");
    return `/journey-test-results/by-merchant?${p.toString()}`;
  }, [filters.tab, filters.regressedOnly, query]);

  const runsUrl = useMemo(
    () =>
      filters.tab === "runs"
        ? `/journey-test-results?${query.toString()}`
        : null,
    [filters.tab, query]
  );

  // Coverage is asked in hours-back because it starts from the estate (maps
  // with no run), where an absolute run window has nothing to match against.
  const coverageUrl = useMemo(() => {
    if (filters.tab !== "coverage") return null;
    const hours = filters.from
      ? Math.max(1, Math.round((Date.now() - new Date(filters.from).getTime()) / HOUR))
      : 168;
    const p = new URLSearchParams();
    p.set("sinceHours", String(Math.min(hours, 2160)));
    if (filters.domain) p.set("domain", filters.domain);
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String(filters.page * PAGE_SIZE));
    return `/journey-test-results/coverage-gaps?${p.toString()}`;
  }, [filters.tab, filters.from, filters.domain, filters.page]);

  const missingUrl = useMemo(() => {
    if (filters.tab !== "coverage") return null;
    const p = new URLSearchParams();
    if (filters.from) p.set("from", filters.from);
    if (filters.to) p.set("to", filters.to);
    if (filters.domain) p.set("domain", filters.domain);
    p.set("limit", String(PAGE_SIZE));
    return `/journey-test-results/missing-verdicts?${p.toString()}`;
  }, [filters.tab, filters.from, filters.to, filters.domain]);

  // The picker's options. Fetched once and left alone — a reviewer switching
  // tabs must not re-request the list index on every render.
  const { data: qaLists } = useGet<IQaReviewListsResponse>(
    "qa-lists?page=1&limit=100&sortBy=name&sortOrder=asc",
    true
  );

  const {
    data: merchants,
    loading: merchantsLoading,
    refetch: refetchMerchants,
  } = useGet<IJourneyMerchantListResponse>(merchantUrl);
  const {
    data: runs,
    loading: runsLoading,
    refetch: refetchRuns,
  } = useGet<IJourneyRunListResponse>(runsUrl);
  const {
    data: coverage,
    loading: coverageLoading,
    refetch: refetchCoverage,
  } = useGet<IJourneyCoverageGapResponse>(coverageUrl);
  const { data: missing, refetch: refetchMissing } =
    useGet<IJourneyMissingVerdictResponse>(missingUrl);

  const loading =
    filters.tab === "merchants"
      ? merchantsLoading
      : filters.tab === "runs"
        ? runsLoading
        : coverageLoading;

  const totals =
    filters.tab === "merchants" ? merchants?.totals : runs?.totals;

  // Rows in run shape, whichever tab is showing — so copy/detail work the same.
  const runRows: IJourneyRunRow[] = useMemo(() => {
    if (filters.tab === "merchants")
      return (merchants?.items ?? []).map((m) => m.latest).filter(Boolean);
    if (filters.tab === "runs") return runs?.items ?? [];
    return [];
  }, [filters.tab, merchants, runs]);

  /** Plain-English restatement of the query, so a reviewer can say what they
   *  reviewed and a pasted report carries its own provenance. */
  const describe = useMemo(() => {
    const bits: string[] = [
      filters.tab === "merchants"
        ? "latest run per merchant"
        : filters.tab === "runs"
          ? "all runs"
          : "maps with no successful run",
    ];
    if (filters.from) bits.push(`since ${new Date(filters.from).toLocaleString()}`);
    if (filters.to) bits.push(`until ${new Date(filters.to).toLocaleString()}`);
    if (filters.domain) bits.push(`domain ~ ${filters.domain}`);
    if (filters.outcomes.length) bits.push(filters.outcomes.join("/"));
    if (filters.regressedOnly) bits.push("regressed only");
    if (filters.ai) bits.push(filters.ai === "yes" ? "AI used" : "no AI");
    if (filters.source) bits.push(filters.source);
    if (filters.device) bits.push(filters.device);
    if (filters.minDurationMs)
      bits.push(`slower than ${fmtDuration(filters.minDurationMs)}`);
    if (filters.minCost) bits.push(`over ${fmtCost(filters.minCost)}`);
    bits.push(`sorted ${filters.sort}`);
    return bits.join(", ");
  }, [filters]);

  // Matched on the shape of the query, not on a stored "which chip did I click"
  // flag — so editing a filter by hand correctly un-highlights the preset.
  const activePreset = useMemo(
    () =>
      PRESETS.find((p) => {
        const f = p.build();
        return (
          f.tab === filters.tab &&
          f.regressedOnly === filters.regressedOnly &&
          f.sort === filters.sort &&
          f.outcomes.join() === filters.outcomes.join()
        );
      })?.key ?? null,
    [filters]
  );

  const refresh = () => {
    refetchMerchants().catch(() => undefined);
    refetchRuns().catch(() => undefined);
    refetchCoverage().catch(() => undefined);
    refetchMissing().catch(() => undefined);
  };

  const total =
    filters.tab === "merchants"
      ? merchants?.total ?? 0
      : filters.tab === "runs"
        ? runs?.total ?? 0
        : coverage?.total ?? 0;

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-start justify-content-between mb-3 gap-2 flex-wrap">
        <div>
          <h4 className="mb-0">Template Results</h4>
          <small className="text-muted">
            Are our templates still working — per run, per merchant, and what it
            cost to find out.
          </small>
        </div>
        <div className="d-flex gap-2">
          <CopyButton
            text={() => rowsToMarkdown(runRows, describe, totals)}
            label={`Copy ${runRows.length} row(s)`}
            disabled={runRows.length === 0}
          />
          <Button variant="outline-secondary" size="sm" onClick={refresh}>
            <RefreshCw size={14} className="me-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Presets — the slices this page is actually opened for */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {PRESETS.map((p) => {
          const Icon = p.icon;
          const active = activePreset === p.key;
          return (
            <OverlayTrigger key={p.key} placement="bottom" overlay={<Tooltip>{p.hint}</Tooltip>}>
              <Button
                size="sm"
                variant={active ? "primary" : "outline-primary"}
                onClick={() => setFilters(p.build())}
              >
                <Icon size={14} className="me-1" />
                {p.label}
              </Button>
            </OverlayTrigger>
          );
        })}
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => setFilters(defaultFilters())}
        >
          Reset
        </Button>
      </div>

      <TotalsRow
        totals={totals}
        loading={loading}
        merchantView={filters.tab === "merchants"}
      />

      {/* Filters */}
      <Card className="mb-3">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-center">
            <Col xs={12} md={2}>
              <Form.Control
                size="sm"
                placeholder="Filter by domain…"
                value={filters.domain}
                onChange={(e) => patch({ domain: e.target.value })}
              />
            </Col>
            <Col xs={12} md={2}>
              <Form.Select
                size="sm"
                value={filters.qaListId}
                title="Narrow to the merchants a QA list names"
                onChange={(e) => patch({ qaListId: e.target.value })}
              >
                <option value="">All QA lists</option>
                {(qaLists?.results ?? []).map((l) => (
                  <option key={l._id} value={l._id}>
                    {l.name} ({l.totalItems})
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={4}>
              <ButtonGroup size="sm">
                {RANGES.map((r) => {
                  const iso = agoIso(r.ms);
                  // Compare on the hour — the stored instant drifts by
                  // milliseconds from a freshly computed one.
                  const active =
                    !!filters.from &&
                    !filters.to &&
                    Math.abs(
                      new Date(filters.from).getTime() - new Date(iso).getTime()
                    ) <
                      HOUR / 2;
                  return (
                    <Button
                      key={r.label}
                      variant={active ? "secondary" : "outline-secondary"}
                      onClick={() => patch({ from: agoIso(r.ms), to: "" })}
                    >
                      {r.label}
                    </Button>
                  );
                })}
              </ButtonGroup>
            </Col>
            <Col xs={6} md={2}>
              <Form.Control
                size="sm"
                type="datetime-local"
                title="From"
                value={isoToLocalInput(filters.from)}
                onChange={(e) =>
                  patch({ from: localInputToIso(e.target.value) })
                }
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Control
                size="sm"
                type="datetime-local"
                title="To"
                value={isoToLocalInput(filters.to)}
                onChange={(e) => patch({ to: localInputToIso(e.target.value) })}
              />
            </Col>
          </Row>

          <Row className="g-2 align-items-center mt-1">
            <Col xs={12} md={4}>
              <div className="d-flex flex-wrap gap-1">
                {OUTCOME_ORDER.map((o) => {
                  const m = OUTCOME_META[o];
                  const active = filters.outcomes.includes(o);
                  return (
                    <Button
                      key={o}
                      size="sm"
                      variant={active ? m.bg : `outline-${m.bg}`}
                      className={m.text === "dark" && active ? "text-dark" : ""}
                      onClick={() => toggleOutcome(o)}
                      disabled={filters.tab === "coverage"}
                    >
                      {m.label}
                      {totals?.byOutcome?.[o] != null && (
                        <span className="ms-1 opacity-75">
                          {totals.byOutcome[o]}
                        </span>
                      )}
                    </Button>
                  );
                })}
              </div>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={filters.ai}
                onChange={(e) => patch({ ai: e.target.value as IFilters["ai"] })}
                disabled={filters.tab === "coverage"}
              >
                <option value="">AI: any</option>
                <option value="yes">AI was used</option>
                <option value="no">No AI</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={filters.source}
                onChange={(e) => patch({ source: e.target.value })}
                disabled={filters.tab === "coverage"}
              >
                <option value="">All sources</option>
                <option value="bespoke">Bespoke</option>
                <option value="template">Template</option>
                <option value="platform_default">Platform default</option>
                <option value="platform_override">Platform + override</option>
                <option value="unmapped">Unmapped</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={String(filters.minDurationMs ?? "")}
                onChange={(e) =>
                  patch({
                    minDurationMs: e.target.value ? Number(e.target.value) : null,
                  })
                }
                disabled={filters.tab === "coverage"}
              >
                {SLOW_OPTIONS.map((s) => (
                  <option key={s.label} value={s.ms ?? ""}>
                    {s.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={filters.sort}
                onChange={(e) =>
                  patch({ sort: e.target.value as TRunSort })
                }
                disabled={filters.tab === "coverage"}
              >
                <option value="recent">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="worst">Most broken first</option>
                <option value="slowest">Slowest first</option>
                <option value="costliest">Most expensive first</option>
              </Form.Select>
            </Col>
          </Row>

          <Row className="g-2 align-items-center mt-1">
            <Col xs={6} md={3}>
              <Form.Select
                size="sm"
                value={filters.device}
                onChange={(e) => patch({ device: e.target.value })}
                disabled={filters.tab === "coverage"}
              >
                <option value="">All devices</option>
                <option value="mobile">Mobile</option>
                <option value="desktop">Desktop</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <Form.Check
                type="switch"
                id="regressed-only"
                label="Regressed only"
                checked={filters.regressedOnly}
                disabled={filters.tab !== "merchants"}
                onChange={(e) => patch({ regressedOnly: e.target.checked })}
              />
            </Col>
            <Col xs={12} md={6} className="text-md-end">
              <small className="text-muted">{describe}</small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabs */}
      <Nav
        variant="tabs"
        className="mb-0"
        activeKey={filters.tab}
        onSelect={(k) => patch({ tab: (k ?? "merchants") as TTab })}
      >
        <Nav.Item>
          <Nav.Link eventKey="merchants">
            Merchants{" "}
            {merchants && (
              <Badge bg="light" text="dark">
                {merchants.total}
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="runs">
            Runs{" "}
            {runs && (
              <Badge bg="light" text="dark">
                {runs.total}
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="coverage">
            Never verified{" "}
            {coverage && (
              <Badge bg="light" text="dark">
                {coverage.total}
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Card className="rounded-top-0">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : filters.tab === "merchants" ? (
            <MerchantTable
              rows={merchants?.items ?? []}
              onSelect={setSelected}
            />
          ) : filters.tab === "runs" ? (
            <RunTable rows={runs?.items ?? []} onSelect={setSelected} />
          ) : (
            <>
              <div className="px-1 pb-2 d-flex align-items-center gap-2">
                <strong className="small">Ran, but wrote no verdict</strong>
                <Badge bg={missing?.total ? "danger" : "secondary"}>
                  {missing?.total ?? 0}
                </Badge>
                <span className="small text-muted">
                  the walk failed before a run could be recorded — these appear
                  on no other tab
                </span>
              </div>
              <MissingVerdictTable rows={missing?.items ?? []} />
              <div className="px-1 pt-4 pb-2 d-flex align-items-center gap-2">
                <strong className="small">Maps with no run in this window</strong>
                <Badge bg="secondary">{coverage?.total ?? 0}</Badge>
              </div>
              <CoverageTable rows={coverage?.items ?? []} />
            </>
          )}
          {!loading && total > PAGE_SIZE && (
            <Pager
              page={filters.page}
              limit={PAGE_SIZE}
              total={total}
              onPage={(p) => setFilters((f) => ({ ...f, page: p }))}
            />
          )}
        </Card.Body>
      </Card>

      <RunDetailModal run={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

// ─── Tables ──────────────────────────────────────────────────────────────────

const MerchantTable: React.FC<{
  rows: IJourneyMerchantRow[];
  onSelect: (r: IJourneyRunRow) => void;
}> = ({ rows, onSelect }) => (
  <Table hover responsive className="mb-0 align-middle">
    <thead>
      <tr>
        <th>Merchant</th>
        <th>View</th>
        <th>Source</th>
        <th>Latest</th>
        <th>Nodes</th>
        <th>Trend</th>
        <th className="text-end">Runs</th>
        <th>AI</th>
        <th className="text-end">Cost (window)</th>
        <th className="text-end">Duration</th>
        <th>Last run</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr
          key={`${row.domain}-${row.templateView}`}
          style={{ cursor: "pointer" }}
          onClick={() => row.latest && onSelect(row.latest)}
        >
          <td>
            {row.domain}
            {row.regressed && (
              <Badge bg="danger" className="ms-2">
                <TrendingDown size={11} className="me-1" />
                regressed
              </Badge>
            )}
          </td>
          <td>
            <div className="small text-muted">{row.templateView}</div>
            <DeviceBadge device={row.latest?.device} />
          </td>
          <td>
            <SourceBadge source={row.latest?.source} />
          </td>
          <td>
            <OutcomeBadge outcome={row.latest?.outcome} />
            {row.previousOutcome && (
              <div className="text-muted small">was {row.previousOutcome}</div>
            )}
          </td>
          <td>{row.latest && <NodeCounts run={row.latest} />}</td>
          <td>
            <HistoryStrip row={row} />
          </td>
          <td className="text-end small">{row.runCount}</td>
          <td>
            <AiBadge used={row.latest?.aiUsed} cost={row.latest?.aiCost} />
          </td>
          <td className="text-end small">{fmtCost(row.totalAiCost)}</td>
          <td className="text-end small">
            {fmtDuration(row.latest?.durationMs)}
          </td>
          <td className="small text-muted text-nowrap">
            {fmtDateShort(row.latest?.createdAt)}
          </td>
        </tr>
      ))}
      {rows.length === 0 && (
        <tr>
          <td colSpan={11} className="text-center text-muted py-4">
            No merchants match this filter.
          </td>
        </tr>
      )}
    </tbody>
  </Table>
);

const RunTable: React.FC<{
  rows: IJourneyRunRow[];
  onSelect: (r: IJourneyRunRow) => void;
}> = ({ rows, onSelect }) => (
  <Table hover responsive className="mb-0 align-middle">
    <thead>
      <tr>
        <th>Ran at</th>
        <th>Merchant</th>
        <th>View</th>
        <th>Source</th>
        <th>Outcome</th>
        <th>Nodes</th>
        <th>AI</th>
        <th className="text-end">Duration</th>
        <th>Note</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((run) => (
        <tr
          key={run._id}
          style={{ cursor: "pointer" }}
          onClick={() => onSelect(run)}
        >
          <td className="small text-muted text-nowrap">
            {fmtDateShort(run.createdAt)}
          </td>
          <td>{run.domain}</td>
          <td>
            <div className="small text-muted">{run.templateView}</div>
            <DeviceBadge device={run.device} />
          </td>
          <td>
            <SourceBadge source={run.source} />
          </td>
          <td>
            <OutcomeBadge outcome={run.outcome} />
            {run.businessFailCount > 0 && (
              <div className="text-danger small">
                {run.businessFailCount} selector rot
              </div>
            )}
          </td>
          <td>
            <NodeCounts run={run} />
          </td>
          <td>
            <AiBadge used={run.aiUsed} cost={run.aiCost} />
          </td>
          <td className="text-end small">{fmtDuration(run.durationMs)}</td>
          <td
            className="small text-muted text-truncate"
            style={{ maxWidth: 260 }}
          >
            {run.note ?? ""}
          </td>
        </tr>
      ))}
      {rows.length === 0 && (
        <tr>
          <td colSpan={9} className="text-center text-muted py-4">
            No runs match this filter.
          </td>
        </tr>
      )}
    </tbody>
  </Table>
);

/**
 * Merchants that ran and wrote nothing. Sits above the coverage table because
 * it is the more urgent list: a map we did not re-verify is stale, a walk that
 * died before writing a verdict is broken, and until this was surfaced the
 * second kind appeared on no tab at all.
 */
const MissingVerdictTable: React.FC<{
  rows: IJourneyMissingVerdictResponse["items"];
}> = ({ rows }) => (
  <Table hover responsive className="mb-0 align-middle">
    <thead>
      <tr>
        <th>Merchant</th>
        <th>Country</th>
        <th>Run</th>
        <th>Failure</th>
        <th className="text-end">Attempts</th>
        <th className="text-end">Cost</th>
        <th>Last attempt</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.domain}>
          <td>
            {r.domain}{" "}
            {!r.everMapped && (
              <Badge bg="danger" className="ms-1">
                never mapped
              </Badge>
            )}
          </td>
          <td className="small">{r.countryCode ?? "—"}</td>
          <td className="small text-muted">
            {r.runType}
            {r.capturePurpose ? ` · ${r.capturePurpose}` : ""}
          </td>
          <td className="small">
            {r.errorType && (
              <Badge bg="warning" text="dark" className="me-1">
                {r.errorType}
              </Badge>
            )}
            <span className="text-muted">{r.errorMessage ?? "—"}</span>
          </td>
          <td className="text-end small">{r.attempts}</td>
          <td className="text-end small">{fmtCost(r.totalCost)}</td>
          <td className="small text-muted">{fmtDateShort(r.lastAttemptAt)}</td>
        </tr>
      ))}
      {rows.length === 0 && (
        <tr>
          <td colSpan={7} className="text-center text-muted py-4">
            Every merchant that ran in this window produced a verdict.
          </td>
        </tr>
      )}
    </tbody>
  </Table>
);

const CoverageTable: React.FC<{
  rows: IJourneyCoverageGapResponse["items"];
}> = ({ rows }) => (
  <Table hover responsive className="mb-0 align-middle">
    <thead>
      <tr>
        <th>Merchant</th>
        <th>View</th>
        <th>Checkout tech</th>
        <th>Country</th>
        <th>Last verified</th>
        <th>Map updated</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((m) => (
        <tr key={m._id}>
          <td>{m.domain}</td>
          <td className="small text-muted">{m.templateView}</td>
          <td className="small">{m.checkoutTechnology ?? "—"}</td>
          <td className="small">{m.countryCode ?? "—"}</td>
          <td className="small text-muted">
            {m.lastVerifiedAt ? (
              fmtDateShort(m.lastVerifiedAt)
            ) : (
              <Badge bg="danger">never</Badge>
            )}
          </td>
          <td className="small text-muted">{fmtDateShort(m.updatedAt)}</td>
        </tr>
      ))}
      {rows.length === 0 && (
        <tr>
          <td colSpan={6} className="text-center text-muted py-4">
            Every served map has a successful run in this window.
          </td>
        </tr>
      )}
    </tbody>
  </Table>
);
